import React, { useEffect, useRef, useState } from 'react';
import { useSubscription } from '../../hooks/useSubscription';
import { useNotification } from '../../contexts/NotificationContext';
import { getApiUrl, getAuthHeaders } from '../../config/api';
import { Video, VideoOff, Play, Square, Users, UserCheck, UserX, Mic, MicOff } from 'lucide-react';
import {
  createPeerConnection,
  getUserMedia,
  addStreamToPeerConnection,
  createOffer,
  handleAnswer,
  addIceCandidate,
  stopStream,
  closePeerConnection,
  SignalingClient,
} from '../../utils/webrtc';

export default function LiveStreamBroadcaster() {
  const videoRef = useRef(null);

  // Media / WebRTC state
  const [stream, setStream] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamData, setStreamData] = useState(null);
  const [signalingClient, setSignalingClient] = useState(null);
  const peerConnectionsRef = useRef(new Map());
  const [connectedViewers, setConnectedViewers] = useState(0);

  // UI / form
  const [streamTitle, setStreamTitle] = useState('');
  const [streamDescription, setStreamDescription] = useState('');
  const [streamQuality, setStreamQuality] = useState('auto');
  const [isLoading, setIsLoading] = useState(false);
  const [cameraPermissionRequested, setCameraPermissionRequested] = useState(false);

  // Campfire groups / viewers / pending
  const [campfireGroups, setCampfireGroups] = useState([]);
  const [selectedCampfireGroup, setSelectedCampfireGroup] = useState('');
  const [showCampfireSelection, setShowCampfireSelection] = useState(false);
  const [viewers, setViewers] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);

  // Debug state
  const [debugLogs, setDebugLogs] = useState([]);

  const { hasFeature } = useSubscription();
  const { showError, showSuccess } = useNotification();

  // Debug logging function
  const addDebugLog = (message) => {
    console.log(message);
    setDebugLogs(prev => [...prev.slice(-10), `${new Date().toLocaleTimeString()}: ${message}`]);
  };

  // ===== Cross-tab "single camera owner" with safe BroadcastChannel =====
  const CAMERA_CHANNEL = 'mytrailer-camera-owner-v1';
  const bcRef = useRef(null);
  const bcOpenRef = useRef(false);
  const heartbeatRef = useRef(null);
  const [iAmOwner, setIAmOwner] = useState(false);
  const [cameraInUseByAnotherTab, setCameraInUseByAnotherTab] = useState(false);

  function openChannel() {
    try {
      if (typeof BroadcastChannel === 'undefined') return false;
      try { bcRef.current?.close(); } catch {}
      bcRef.current = new BroadcastChannel(CAMERA_CHANNEL);
      bcOpenRef.current = true;
      bcRef.current.onmessage = (e) => {
        const msg = e.data;
        if (!msg) return;
        if (msg.type === 'request-ownership' && iAmOwner) {
          stopCameraAndBroadcastRelease();
        } else if (msg.type === 'ownership-claimed' && !iAmOwner) {
          setCameraInUseByAnotherTab(true);
        } else if (msg.type === 'ownership-released') {
          setCameraInUseByAnotherTab(false);
        } else if (msg.type === 'heartbeat' && !iAmOwner) {
          setCameraInUseByAnotherTab(true);
        }
      };
      return true;
    } catch (err) {
      console.warn('BroadcastChannel open failed; no cross-tab coordination.', err);
      bcRef.current = null;
      bcOpenRef.current = false;
      return false;
    }
  }
  function closeChannel() {
    stopHeartbeating();
    try { bcRef.current?.close(); } catch {}
    bcRef.current = null;
    bcOpenRef.current = false;
  }
  function bcPost(message) {
    try {
      if (!bcOpenRef.current || !bcRef.current) {
        if (!openChannel()) return;
      }
      bcRef.current.postMessage(message);
    } catch (err) {
      if (String(err).includes('InvalidStateError')) {
        if (openChannel()) { try { bcRef.current.postMessage(message); } catch {} }
      } else {
        console.warn('BroadcastChannel post failed:', err);
      }
    }
  }
  function startHeartbeating() {
    stopHeartbeating();
    heartbeatRef.current = setInterval(() => bcPost({ type: 'heartbeat' }), 1500);
  }
  function stopHeartbeating() {
    if (heartbeatRef.current) clearInterval(heartbeatRef.current);
    heartbeatRef.current = null;
  }
  function broadcastClaim() { bcPost({ type: 'ownership-claimed' }); }
  function broadcastRelease() { bcPost({ type: 'ownership-released' }); }
  function requestOwnershipFromOthers() { bcPost({ type: 'request-ownership' }); }
  function markAsOwner(on) {
    setIAmOwner(on);
    if (on) { setCameraInUseByAnotherTab(false); startHeartbeating(); broadcastClaim(); }
    else { stopHeartbeating(); }
  }
  function stopCameraAndBroadcastRelease() {
    try {
      peerConnectionsRef.current.forEach((pc) => {
        try { pc.getSenders().forEach((s) => s.replaceTrack(null)); } catch {}
      });
      if (stream) {
        console.log('Stopping and clearing stream in stopCameraAndBroadcastRelease');
        stopStream(stream);
        setStream(null); // Clear the React state
      }
      if (videoRef.current) {
        console.log('Clearing video element srcObject');
        videoRef.current.srcObject = null;
      }
    } finally {
      markAsOwner(false);
      broadcastRelease();
      // Reset camera permission state so it can be requested again on mobile
      setCameraPermissionRequested(false);
    }
  }

  // Init / cleanup BC
  useEffect(() => {
    openChannel();
    return () => closeChannel();
  }, []);

  // Release on pagehide (iOS friendly)
  useEffect(() => {
    const onPageHide = () => { if (iAmOwner) stopCameraAndBroadcastRelease(); };
    window.addEventListener('pagehide', onPageHide);
    return () => window.removeEventListener('pagehide', onPageHide);
  }, [iAmOwner]);

  // Warn on unload if live
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (streamData && (streamData.status === 'active' || streamData.status === 'live')) {
        e.preventDefault();
        e.returnValue = 'Leave? Your live stream will end.';
        return 'Leave? Your live stream will end.';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [streamData]);

  // ===== Initial fetches + mobile HTTPS guard =====
  useEffect(() => {
    if (!hasFeature('live_streaming')) return;

    fetchCampfireGroups();

    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const isHTTPS = window.location.protocol === 'https:';
    if (isMobile && !isHTTPS) {
      showError('Camera access requires HTTPS on mobile devices.');
      return;
    }
    // Desktop: optional auto-prepare stream preview
    if (!isMobile) {
      // don’t auto-open camera on mobile; wait for tap
      // (no-op here; user will click “Enable Camera”)
    }
    // cleanup on unmount
    return () => {
      try {
        peerConnectionsRef.current.forEach((pc) => closePeerConnection(pc));
        peerConnectionsRef.current.clear();
      } catch {}
      try { signalingClient?.disconnect(); } catch {}
      if (iAmOwner) stopCameraAndBroadcastRelease();
    };
  }, []);

  // ===== Poll viewers/requests while streaming =====
  useEffect(() => {
    if (!streamData || !isStreaming) return;
    const id = setInterval(() => {
      fetchPendingRequests();
      fetchViewers();
    }, 2000);
    return () => clearInterval(id);
  }, [streamData, isStreaming]);

  // ===== Page visibility handler for mobile resilience =====
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Page is hidden - release camera if not streaming
        if (iAmOwner && !isStreaming) {
          stopCameraAndBroadcastRelease();
        }
      } else {
        // Page is visible again - on mobile, the camera overlay will show "Enable Camera" button
        // because cameraPermissionRequested will be false after stopCameraAndBroadcastRelease
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [iAmOwner, isStreaming]);

  // ===== Click outside handler for campfire dropdown =====
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showCampfireSelection && !event.target.closest('.campfire-dropdown')) {
        setShowCampfireSelection(false);
      }
    };

    if (showCampfireSelection) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showCampfireSelection]);

  // ===== Camera access (ownership aware) =====
  const requestCameraAccess = async () => {
    const isMobile = /Android|iPhone|iPad/i.test(navigator.userAgent);
    const quality = isMobile ? 'medium' : streamQuality;
    const constraints = { video: { facingMode: isMobile ? 'environment' : 'user' }, audio: true };

    const openAndClaim = async () => {
      if (stream) {
        console.log('Stopping existing stream before requesting new access');
        stopStream(stream);
        setStream(null);
        if (videoRef.current) videoRef.current.srcObject = null;
        await new Promise((r) => setTimeout(r, 100));
      }
      console.log('Requesting getUserMedia with constraints:', constraints);
      const mediaStream = await getUserMedia(constraints, quality);
      console.log('Got media stream:', mediaStream);
      setStream(mediaStream);
      if (videoRef.current) {
        console.log('Setting video srcObject to new stream');
        videoRef.current.srcObject = mediaStream;
      } else {
        console.log('Video ref not available!');
      }
      setCameraPermissionRequested(true);
      const audioTracks = mediaStream.getAudioTracks();
      if (audioTracks.length > 0) setIsMuted(!audioTracks[0].enabled);
      markAsOwner(true);
      console.log('Camera access completed, stream set:', !!mediaStream);
    };

    try {
      requestOwnershipFromOthers();
      await new Promise((r) => setTimeout(r, 500));
      await openAndClaim();
    } catch (err) {
      console.error('getUserMedia failed:', err);
      if (err.name === 'NotReadableError' || err.name === 'AbortError') {
        setCameraInUseByAnotherTab(true);
        showError('Camera is in use in another tab or app.');
      } else if (err.name === 'NotAllowedError') {
        showError('Please allow camera/microphone permissions.');
      } else if (err.name === 'OverconstrainedError') {
        showError('Camera settings not supported; try a lower quality.');
      } else {
        showError('Unable to access camera/microphone.');
      }
    }
  };

  // ===== Campfire groups =====
  const fetchCampfireGroups = async () => {
    try {
      let response = await fetch(getApiUrl('/api/streaming/campfire-groups'), {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setCampfireGroups(data.campfireGroups || []);
        return;
      }
      // Fallback to connect endpoint
      response = await fetch(getApiUrl('/api/connect/conversations'), {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        const groups = data
          .filter((conv) => conv.isGroup === true)
          .map((conv) => ({
            id: conv.id,
            name: conv.name,
            description: conv.description,
            theme: conv.theme || 'campfire',
            participantCount: conv.participantCount || 0,
            userRole: conv.userRole || 'member',
            created_at: conv.created_at,
            updated_at: conv.updated_at,
          }));
        setCampfireGroups(groups);
      }
    } catch (e) {
      console.error('Error fetching campfire groups:', e);
    }
  };

  const sendToCampfire = async (campfireGroupId) => {
    if (!streamData) return;
    try {
      const res = await fetch(getApiUrl('/api/streaming/send-to-campfire'), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          campfire_group_id: campfireGroupId,
          stream_id: streamData.id,
          access_code: streamData.access_code,
          stream_title: streamData.title,
        }),
      });
      if (res.ok) showSuccess('Streaming code sent to campfire group!');
      else {
        const data = await res.json();
        showError(`Failed to send to campfire: ${data.error || 'Unknown error'}`);
      }
    } catch {
      showError('Failed to send to campfire group');
    }
  };

  // ===== Stream server calls =====
  const createStream = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(getApiUrl('/api/streaming/create'), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          title: streamTitle || 'My Live Stream',
          description: streamDescription || 'Live streaming from MyTrailer',
          max_viewers: 50,
          is_public: false,
          campfire_group_id: selectedCampfireGroup || null,
        }),
      });
      const ct = response.headers.get('content-type') || '';
      if (!ct.includes('application/json')) {
        showError(`Server returned non-JSON (status ${response.status}).`);
        return;
      }
      const data = await response.json();
      if (response.ok) {
        setStreamData(data.stream);
        const msg = selectedCampfireGroup
          ? `Stream created and sent to campfire group! Access code: ${data.stream.access_code}`
          : `Stream created! Share this access code: ${data.stream.access_code}`;
        showSuccess(msg);
      } else {
        showError(data.error || 'Failed to create stream');
      }
    } catch (e) {
      console.error(e);
      showError('Failed to create stream');
    } finally {
      setIsLoading(false);
    }
  };

  const refreshStreamData = async () => {
    if (!streamData) return;
    try {
      const res = await fetch(getApiUrl(`/api/streaming/${streamData.id}`), {
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setStreamData(data.stream);
      }
    } catch {}
  };

  const startStreaming = async () => {
    if (!streamData) return;
    
    console.log('Starting stream - current stream state:', !!stream);
    console.log('Video element srcObject:', videoRef.current?.srcObject);
    
    // Check if we have a camera stream, if not, request it
    if (!stream) {
      try {
        console.log('No camera stream found, requesting camera access...');
        await requestCameraAccess();
        // Wait a moment for the stream to be set and video element to update
        await new Promise(resolve => setTimeout(resolve, 200));
        
        console.log('After camera access - stream state:', !!stream);
        console.log('Video element srcObject after access:', videoRef.current?.srcObject);
        
        // Double-check that we have a stream and video element is updated
        if (!stream) {
          showError('Failed to acquire camera access');
          return;
        }
        
        if (videoRef.current && !videoRef.current.srcObject) {
          console.log('Setting video srcObject after camera access');
          videoRef.current.srcObject = stream;
        }
      } catch (error) {
        console.error('Camera access error:', error);
        showError('Camera access required to start streaming');
        return;
      }
    } else {
      // We have a stream, but make sure video element is updated
      if (videoRef.current && !videoRef.current.srcObject) {
        console.log('Stream exists but video element not updated, fixing...');
        videoRef.current.srcObject = stream;
      }
    }
    
    setIsLoading(true);
    try {
      const res = await fetch(getApiUrl(`/api/streaming/${streamData.id}/start`), {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        setIsStreaming(true);
        showSuccess('Stream started!');
        await refreshStreamData();
        await setupWebRTC();
      } else {
        const data = await res.json();
        showError(data.error || 'Failed to start stream');
      }
    } catch (e) {
      console.error(e);
      showError('Failed to start stream');
    } finally {
      setIsLoading(false);
    }
  };

  const endStreaming = async () => {
    if (!streamData) return;
    setIsLoading(true);
    try {
      // Tear down WebRTC first
      peerConnectionsRef.current.forEach((pc) => closePeerConnection(pc));
      peerConnectionsRef.current.clear();
      if (signalingClient) {
        try { signalingClient.leave(); signalingClient.disconnect(); } catch {}
        setSignalingClient(null);
      }
      if (iAmOwner) stopCameraAndBroadcastRelease();

      const res = await fetch(getApiUrl(`/api/streaming/${streamData.id}/end`), {
        method: 'POST',
        headers: getAuthHeaders(),
      });

      if (res.ok) {
        setIsStreaming(false);
        showSuccess('Stream ended.');
        await refreshStreamData();
        setViewers([]);
        setPendingRequests([]);
      } else {
        const data = await res.json();
        showError(data.error || 'Failed to end stream');
      }
    } catch (e) {
      console.error(e);
      showError('Failed to end stream');
    } finally {
      setIsLoading(false);
    }
  };

  const startNewStream = async () => {
    // Clean up current stream state
    setStreamData(null);
    setViewers([]);
    setPendingRequests([]);
    setStreamTitle('');
    setStreamDescription('');
    
    // If we don't have a camera stream, request it
    if (!stream) {
      try {
        console.log('Starting new stream - requesting camera access...');
        await requestCameraAccess();
        // Wait for stream to be set
        await new Promise(resolve => setTimeout(resolve, 200));
        
        if (videoRef.current && !videoRef.current.srcObject) {
          console.log('Setting video srcObject for new stream');
          videoRef.current.srcObject = stream;
        }
      } catch (error) {
        console.error('Camera access error for new stream:', error);
        showError('Camera access required to start new stream');
        return;
      }
    }
    
    showSuccess('Ready to start a new stream!');
  };

  // ===== Requests / viewers =====
  const approveViewer = async (viewerId, approved) => {
    if (!streamData) return;
    try {
      const res = await fetch(getApiUrl(`/api/streaming/${streamData.id}/approve/${viewerId}`), {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ approved }),
      });
      if (res.ok) {
        showSuccess(approved ? 'Viewer approved' : 'Viewer denied');
        fetchPendingRequests();
        fetchViewers();
      } else {
        const data = await res.json();
        showError(data.error || 'Failed to update viewer access');
      }
    } catch {
      showError('Failed to update viewer access');
    }
  };

  const fetchPendingRequests = async () => {
    if (!streamData) return;
    try {
      const res = await fetch(getApiUrl(`/api/streaming/${streamData.id}/requests`), {
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setPendingRequests(data.requests);
      }
    } catch (e) {
      console.error('Error fetching requests:', e);
    }
  };

  const fetchViewers = async () => {
    if (!streamData) return;
    try {
      const res = await fetch(getApiUrl(`/api/streaming/${streamData.id}/viewers`), {
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setViewers(data.viewers);
      }
    } catch (e) {
      console.error('Error fetching viewers:', e);
    }
  };

  // ===== Signaling & per-viewer PCs =====
  const setupWebRTC = async () => {
    if (!streamData || !stream) {
      console.error('No stream data or local media stream.');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const payload = JSON.parse(atob(token.split('.')[1]));
      const userId = payload.userId || payload.user_id || payload.id;

      const client = new SignalingClient(streamData.id, userId, token, 'broadcaster');

      client.onMessage = async (message) => {
        console.log('Broadcaster received message:', message.type, message);
        console.log('Current broadcaster connectionId:', client.connectionId);
        console.log('Message connectionId:', message.connectionId);
        addDebugLog(`Received: ${message.type} from ${message.connectionId || 'server'}`);
        
        if (message.type === 'error') {
          showError(`Server error: ${message.error}`);
          return;
        }
        switch (message.type) {
          case 'joined': {
            console.log('Broadcaster joined, connectionId:', message.connectionId);
            client.connectionId = message.connectionId;
            addDebugLog(`Broadcaster connection ID set: ${message.connectionId}`);
            break;
          }

          case 'viewer-joined': {
            console.log('Viewer joined, creating peer connection for:', message.connectionId);
            addDebugLog(`Viewer joined: ${message.connectionId}`);
            setConnectedViewers((v) => v + 1);
            const viewerId = message.connectionId;

            console.log('Creating peer connection for viewer:', viewerId);
            const pc = createPeerConnection(
              (candidate) => {
                console.log('Sending ICE candidate to viewer:', viewerId);
                addDebugLog(`Sending ICE to ${viewerId}`);
                client.sendIceCandidate(candidate, viewerId);
              }
            );

            // Add connection state monitoring
            pc.onconnectionstatechange = () => {
              console.log(`Peer connection state for ${viewerId}:`, pc.connectionState);
              addDebugLog(`PC state for ${viewerId}: ${pc.connectionState}`);
            };

            pc.oniceconnectionstatechange = () => {
              console.log(`ICE connection state for ${viewerId}:`, pc.iceConnectionState);
              addDebugLog(`ICE state for ${viewerId}: ${pc.iceConnectionState}`);
            };

            console.log('Adding stream to peer connection for viewer:', viewerId);
            addStreamToPeerConnection(pc, stream);
            peerConnectionsRef.current.set(viewerId, pc);

            console.log('Creating offer for viewer:', viewerId);
            addDebugLog(`Creating offer for ${viewerId}`);
            const offer = await createOffer(pc);
            console.log('Offer created, sending to viewer:', viewerId);
            addDebugLog(`Sending offer to ${viewerId}`);
            client.sendOffer(offer, viewerId);
            console.log('Offer sent to viewer:', viewerId);
            break;
          }

          case 'user-left': {
            setConnectedViewers((v) => Math.max(0, v - 1));
            const id = message.connectionId;
            if (id && peerConnectionsRef.current.has(id)) {
              closePeerConnection(peerConnectionsRef.current.get(id));
              peerConnectionsRef.current.delete(id);
            }
            break;
          }

          case 'answer': {
            const id = message.fromConnectionId;
            if (id && peerConnectionsRef.current.has(id)) {
              await handleAnswer(peerConnectionsRef.current.get(id), message.answer);
            }
            break;
          }

          case 'ice-candidate': {
            const id = message.fromConnectionId;
            console.log('Received ICE candidate from:', id, 'Candidate:', message.candidate);
            if (id && peerConnectionsRef.current.has(id)) {
              const pc = peerConnectionsRef.current.get(id);
              console.log('Adding ICE candidate to peer connection for:', id, 'PC state:', pc.connectionState);
              try {
                await addIceCandidate(pc, message.candidate);
                console.log('ICE candidate added successfully for:', id);
              } catch (error) {
                console.error('Failed to add ICE candidate for:', id, error);
              }
            } else {
              console.error('No peer connection found for ICE candidate from:', id);
            }
            break;
          }

          default:
            break;
        }
      };

      client.onError = (err) => { console.error('Signaling error', err); showError('WebRTC signaling error'); };
      client.onClose = () => { setConnectedViewers(0); };

      console.log('Broadcaster connecting to signaling server...');
      await client.connect();
      console.log('Broadcaster signaling client connected');
      setSignalingClient(client);
    } catch (e) {
      console.error('setupWebRTC failed', e);
      showError('Failed to set up streaming');
    }
  };


  // ===== Component unmount cleanup =====
  useEffect(() => {
    return () => {
      try {
        peerConnectionsRef.current.forEach((pc) => closePeerConnection(pc));
        peerConnectionsRef.current.clear();
      } catch {}
      try { signalingClient?.disconnect(); } catch {}
      if (iAmOwner) stopCameraAndBroadcastRelease();
      closeChannel();
    };
  }, []);

  // ===== Mic toggle =====
  const toggleMicrophone = () => {
    if (!stream) return;
    const audioTracks = stream.getAudioTracks();
    if (!audioTracks.length) return;
    const t = audioTracks[0];
    t.enabled = !t.enabled;
    setIsMuted(!t.enabled);
    peerConnectionsRef.current.forEach((pc) => {
      const sender = pc.getSenders().find((s) => s.track && s.track.kind === 'audio');
      if (sender) sender.replaceTrack(t);
    });
    showSuccess(t.enabled ? 'Microphone unmuted' : 'Microphone muted');
  };

  // ===== Basic guards =====
  if (!hasFeature('live_streaming')) {
    return (
      <div className="text-center p-8">
        <Video className="w-16 h-16 mx-auto text-gray-400 mb-4" />
        <h2 className="text-xl font-semibold mb-4">Live Streaming Not Available</h2>
        <p className="text-gray-600">You need a Premium subscription to use live streaming features.</p>
      </div>
    );
  }
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  const isHTTPS = window.location.protocol === 'https:';
  if (isMobile && !isHTTPS) {
    return (
      <div className="text-center p-8">
        <Video className="w-16 h-16 mx-auto text-gray-400 mb-4" />
        <h2 className="text-xl font-semibold mb-4">HTTPS Required for Mobile</h2>
        <p className="text-gray-600 mb-4">Camera access requires a secure connection on mobile devices.</p>
      </div>
    );
  }

  // ===== Camera overlay (enable / take control) =====
  const cameraOverlay = !stream && (
    <div className="absolute inset-0 flex items-center justify-center bg-gray-800">
      <div className="text-center text-white">
        <VideoOff className="w-12 h-12 mx-auto mb-2" />
        <p className="mb-4">
          {cameraInUseByAnotherTab ? 'Camera is in use in another tab.' : 'Camera not available'}
        </p>
        {!cameraPermissionRequested && !cameraInUseByAnotherTab && (
          <button
            onClick={requestCameraAccess}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center mx-auto"
          >
            <Video className="w-4 h-4 mr-2" />
            Enable Camera
          </button>
        )}
        {cameraInUseByAnotherTab && (
          <button
            onClick={async () => {
              requestOwnershipFromOthers();
              await new Promise((r) => setTimeout(r, 600));
              requestCameraAccess();
            }}
            className="bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600 flex items-center mx-auto"
          >
            Take Control
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto p-6">
      <h1 className="text-3xl font-bold mb-6 flex items-center gap-3">
        <Video className="w-8 h-8" />
        Live Streaming
      </h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Video Preview */}
        <div className="lg:col-span-2">
          <div className="bg-black rounded-lg overflow-hidden relative">
            <video ref={videoRef} autoPlay muted className="w-full h-64 lg:h-96 object-cover" />
            {stream && isStreaming && (
              <div className="absolute top-4 right-4">
                <button
                  onClick={toggleMicrophone}
                  className={`p-3 rounded-full transition-colors ${
                    isMuted
                      ? 'bg-red-600 text-white hover:bg-red-700'
                      : 'bg-white text-gray-700 hover:bg-gray-100'
                  }`}
                  title={isMuted ? 'Unmute microphone' : 'Mute microphone'}
                >
                  {isMuted ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
                </button>
              </div>
            )}
            {cameraOverlay}
          </div>
        </div>

        {/* Stream Controls */}
        <div className="space-y-4">
          {!streamData ? (
            <div className="space-y-4">
              <div className="bg-white p-4 rounded-lg border">
                <h3 className="font-semibold mb-3">Create New Stream</h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Stream Title *</label>
                    <input
                      type="text"
                      value={streamTitle}
                      onChange={(e) => setStreamTitle(e.target.value)}
                      placeholder="Enter stream title..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description (Optional)</label>
                    <textarea
                      value={streamDescription}
                      onChange={(e) => setStreamDescription(e.target.value)}
                      placeholder="Enter stream description..."
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Stream Quality</label>
                    <select
                      value={streamQuality}
                      onChange={(e) => setStreamQuality(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="auto">Auto (Best for WiFi)</option>
                      <option value="high">High (1080p - Fast Internet)</option>
                      <option value="medium">Medium (720p - Recommended)</option>
                      <option value="low">Low (480p - Slow Connection)</option>
                    </select>
                  </div>

                  {/* Campfire selector */}
                  <div className="relative campfire-dropdown">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notify Campfire Group (Optional)</label>
                    <button
                      type="button"
                      onClick={() => setShowCampfireSelection(!showCampfireSelection)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-left flex items-center justify-between"
                    >
                      <span>
                        {selectedCampfireGroup
                          ? campfireGroups.find((g) => g.id === selectedCampfireGroup)?.name || 'Select a campfire group'
                          : 'Select a campfire group'}
                      </span>
                      <svg
                        className={`w-4 h-4 transition-transform ${showCampfireSelection ? 'rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>

                    {showCampfireSelection && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedCampfireGroup('');
                            setShowCampfireSelection(false);
                          }}
                          className={`w-full px-3 py-2 text-left hover:bg-gray-100 ${
                            !selectedCampfireGroup ? 'bg-blue-50 text-blue-700' : ''
                          }`}
                        >
                          No campfire group
                        </button>
                        {campfireGroups.map((group) => (
                          <button
                            key={group.id}
                            type="button"
                            onClick={() => {
                              setSelectedCampfireGroup(group.id);
                              setShowCampfireSelection(false);
                            }}
                            className={`w-full px-3 py-2 text-left hover:bg-gray-100 flex items-center justify-between ${
                              selectedCampfireGroup === group.id ? 'bg-blue-50 text-blue-700' : ''
                            }`}
                          >
                            <div>
                              <div className="font-medium">{group.name}</div>
                              <div className="text-xs text-gray-500">{group.participantCount} members</div>
                            </div>
                            <div className="text-xs text-gray-400">🔥</div>
                          </button>
                        ))}
                        {campfireGroups.length === 0 && (
                          <div className="px-3 py-2 text-gray-500 text-sm">No campfire groups available</div>
                        )}
                      </div>
                    )}
                    <p className="text-xs text-gray-500 mt-1">🔥 Send streaming code to a campfire group when stream starts</p>
                  </div>
                </div>
              </div>
              <button
                onClick={createStream}
                disabled={isLoading || !stream || !streamTitle.trim()}
                className="w-full bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {isLoading ? 'Creating...' : 'Create Stream'}
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-gray-100 p-4 rounded-lg">
                <h3 className="font-semibold mb-2">Stream Details</h3>
                <div className="space-y-2 text-sm">
                  <p><strong>Title:</strong> {streamData.title}</p>
                  {streamData.description && <p><strong>Description:</strong> {streamData.description}</p>}
                  <p>
                    <strong>Access Code:</strong>{' '}
                    <span className="font-mono bg-white px-2 py-1 rounded">{streamData.access_code}</span>
                  </p>
                  <p>
                    <strong>Status:</strong>{' '}
                    <span
                      className={`px-2 py-1 rounded text-xs ${
                        streamData.status === 'live'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}
                    >
                      {streamData.status}
                    </span>
                  </p>
                  <p><strong>Max Viewers:</strong> {streamData.max_viewers}</p>
                </div>
              </div>

              {/* Send to Campfire Section */}
              {campfireGroups.length > 0 && (
                <div className="bg-white p-4 rounded-lg border">
                  <h3 className="font-semibold mb-3">🔥 Send to Campfire Group</h3>
                  {selectedCampfireGroup ? (
                    // Show selected group info
                    <div className="space-y-2">
                      <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                        <div>
                          <div className="font-medium text-green-800">
                            {campfireGroups.find(g => g.id === selectedCampfireGroup)?.name}
                          </div>
                          <div className="text-sm text-green-600">
                            Code sent to this group when stream was created
                          </div>
                        </div>
                        <div className="text-green-600">✅</div>
                      </div>
                      <button
                        onClick={() => sendToCampfire(selectedCampfireGroup)}
                        className="w-full bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
                      >
                        Send Code Again
                      </button>
                    </div>
                  ) : (
                    // Show list of groups to choose from
                    <div className="space-y-2">
                      <p className="text-sm text-gray-600 mb-3">Share your streaming code with a campfire group</p>
                      <div className="grid grid-cols-1 gap-2">
                        {campfireGroups.map((group) => (
                          <button
                            key={group.id}
                            onClick={() => sendToCampfire(group.id)}
                            className="flex items-center justify-between p-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                          >
                            <div className="text-left">
                              <div className="font-medium text-sm">{group.name}</div>
                              <div className="text-xs text-gray-500">{group.participantCount} members</div>
                            </div>
                            <div className="text-blue-600 text-sm">Send Code</div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {!isStreaming ? (
                <div className="space-y-2">
                  <button
                    onClick={startStreaming}
                    disabled={isLoading}
                    className="w-full bg-green-600 text-white px-4 py-3 rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center justify-center"
                  >
                    <Play className="w-4 h-4 mr-2" />
                    {isLoading ? 'Starting...' : 'Start Streaming'}
                  </button>
                  <button
                    onClick={startNewStream}
                    disabled={isLoading}
                    className="w-full bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center"
                  >
                    <Video className="w-4 h-4 mr-2" />
                    Start New Stream
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  <button
                    onClick={endStreaming}
                    disabled={isLoading}
                    className="w-full bg-red-600 text-white px-4 py-3 rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center justify-center"
                  >
                    <Square className="w-4 h-4 mr-2" />
                    {isLoading ? 'Ending...' : 'End Stream'}
                  </button>
                </div>
              )}

              {/* Current Viewers */}
              {isStreaming && (
                <div className="bg-gray-100 p-4 rounded-lg">
                  <h3 className="font-semibold mb-2 flex items-center">
                    <Users className="w-4 h-4 mr-2" />
                    Current Viewers ({viewers.length})
                    {connectedViewers > 0 && (
                      <span className="ml-2 text-sm text-green-600">({connectedViewers} connected)</span>
                    )}
                  </h3>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {viewers.map((viewer) => (
                      <div
                        key={viewer.id}
                        className="text-sm text-gray-600 flex justify-between items-center"
                      >
                        <span>{viewer.name}</span>
                        <span className="text-xs text-gray-400">
                          {new Date(viewer.joined_at).toLocaleTimeString()}
                        </span>
                      </div>
                    ))}
                    {viewers.length === 0 && (
                      <p className="text-sm text-gray-500">No viewers yet</p>
                    )}
                  </div>
                </div>
              )}

              {/* Pending Viewer Requests */}
              {isStreaming && pendingRequests.length > 0 && (
                <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                  <h3 className="font-semibold mb-2 text-yellow-800">
                    Pending Requests ({pendingRequests.length})
                  </h3>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {pendingRequests.map((request) => (
                      <div
                        key={request.id}
                        className="flex justify-between items-center py-2 border-b border-yellow-200 last:border-b-0"
                      >
                        <div>
                          <span className="text-sm font-medium">{request.name}</span>
                          <p className="text-xs text-gray-500">{request.email}</p>
                        </div>
                        <div className="space-x-1">
                          <button
                            onClick={() => approveViewer(request.viewer_id, true)}
                            className="bg-green-500 text-white px-2 py-1 rounded text-xs hover:bg-green-600 flex items-center"
                          >
                            <UserCheck className="w-3 h-3 mr-1" />
                            Approve
                          </button>
                          <button
                            onClick={() => approveViewer(request.viewer_id, false)}
                            className="bg-red-500 text-white px-2 py-1 rounded text-xs hover:bg-red-600 flex items-center"
                          >
                            <UserX className="w-3 h-3 mr-1" />
                            Deny
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

    </div>
  );
}
